import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash-es';
import useImmer from '@/hooks/useImmer';
import type { PrefilledText } from '@src/components/pc/Claw/chat/Chatbox';
import type { GuideTipItem, TemplateNode } from '@src/components/pc/Claw/chat/guideTipConfig';
import {
  getGroupClawCountApi,
  getInKimGroupApi,
  postJoinMoltBookGroupApi,
} from '@src/services/walle';
import {
  getKimoInstallCheckApi,
  getKimoInstallCountApi,
  postKimoInstallApi,
} from '@src/services/kimo';
import useUserInfo from '@src/stores/userModel';
import { Toast } from '@kim/ui';

const JOINGROUP_KIM_GROUP_ID = '6737257463915091';
export const GUIDE_PLACEHOLDER_DEFAULT =
  '今天有什么我可以帮你的？（输入 @ 引用内容）';
const WB_EMPLOYEE_CODES = [3, 12, 13, 14, 18, 20, 21];

type GuidePlaceholderState = {
  activeSuggestionItem: GuideTipItem | null;
  prefilledText: PrefilledText | null;
  activeTemplate: TemplateNode[] | null;
  clearEditorNonce: number;
  isEditorEmpty: boolean;
  lastAppliedSignature: string | null;
  placeholder: string;
  joinGroupKimoModalVisible: boolean;
  pendingJoinGroupItem: GuideTipItem | null;
  isInJoingroup: boolean;
  kimoSkillStatus: boolean;
  kimoJoiningLoading: boolean;
  groupBotNumber: number;
  kimoBotNumber: number;
};

const createInitialGuidePlaceholderState = (
  chatBoxPlaceholderProp: string | undefined,
): GuidePlaceholderState => ({
  activeSuggestionItem: null,
  prefilledText: null,
  activeTemplate: null,
  clearEditorNonce: 0,
  isEditorEmpty: true,
  lastAppliedSignature: null,
  placeholder: chatBoxPlaceholderProp ?? GUIDE_PLACEHOLDER_DEFAULT,
  joinGroupKimoModalVisible: false,
  pendingJoinGroupItem: null,
  isInJoingroup: false,
  kimoSkillStatus: false,
  kimoJoiningLoading: false,
  groupBotNumber: 0,
  kimoBotNumber: 0,
});

export interface UseChatListGuidePlaceholderParams {
  suggestionsRef: RefObject<HTMLDivElement | null>;
  onSendMessage: (message: string) => void;
  isShowPlaceholder?: boolean;
  chatBoxPlaceholder?: string;
}

export function useChatListGuidePlaceholder({
  suggestionsRef,
  onSendMessage,
  isShowPlaceholder,
  chatBoxPlaceholder,
}: UseChatListGuidePlaceholderParams) {
  const userInfo = useUserInfo((s) => s.userInfo);
  const isWbOrFl = useMemo(() => {
    const employeeCode = Number((userInfo as any)?.employee_code);
    return (
      (userInfo as any)?.front_line === true || WB_EMPLOYEE_CODES.includes(employeeCode)
    );
  }, [userInfo]);

  const [guideState, updateGuideState] = useImmer<GuidePlaceholderState>(() =>
    createInitialGuidePlaceholderState(chatBoxPlaceholder),
  );

  const handleSubmit = useCallback(
    async (msg: string) => {
      onSendMessage(msg);
    },
    [onSendMessage],
  );
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  const debouncedSubmitJoingroup = useMemo(
    () =>
      debounce((text: string) => {
        handleSubmitRef.current(text);
      }, 600),
    [],
  );

  useEffect(() => {
    updateGuideState((draft) => {
      if (isShowPlaceholder) {
        draft.placeholder = chatBoxPlaceholder ?? GUIDE_PLACEHOLDER_DEFAULT;
        return;
      }
      draft.activeSuggestionItem = null;
      draft.activeTemplate = null;
      draft.prefilledText = null;
      draft.clearEditorNonce += 1;
      draft.placeholder = chatBoxPlaceholder ?? GUIDE_PLACEHOLDER_DEFAULT;
      draft.lastAppliedSignature = null;
      draft.joinGroupKimoModalVisible = false;
      draft.pendingJoinGroupItem = null;
    });
  }, [isShowPlaceholder, chatBoxPlaceholder, updateGuideState]);

  useEffect(() => {
    if (!guideState.activeSuggestionItem) return;
    const handler = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node)
      ) {
        updateGuideState((d) => {
          d.activeSuggestionItem = null;
        });
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [guideState.activeSuggestionItem, suggestionsRef, updateGuideState]);

  const applyTemplate = useCallback(
    (item: GuideTipItem) => {
      updateGuideState((draft) => {
        const signature = [
          item.id,
          item.type ?? 'none',
          item.prompt ?? '',
          item.placeholder ?? '',
        ].join(':');
        if (signature === draft.lastAppliedSignature && !draft.isEditorEmpty) return;

        draft.activeSuggestionItem = null;
        draft.activeTemplate = null;
        draft.prefilledText = null;
        draft.clearEditorNonce += 1;
        draft.placeholder = GUIDE_PLACEHOLDER_DEFAULT;
        if (item.type === 'template') {
          draft.activeTemplate = item.template ? [...item.template] : null;
        }
        if (item.type === 'placeholder') {
          draft.placeholder = item.placeholder ?? '';
        }
        draft.lastAppliedSignature = signature;
      });
    },
    [updateGuideState],
  );

  const onSelectItem = useCallback(
    async (item: GuideTipItem) => {
      if (item.type === 'suggestion' && item.suggestions?.length) {
        updateGuideState((d) => {
          d.activeSuggestionItem = item;
        });
        return;
      }

      if (item.type === 'prompt') {
        handleSubmit(item.prompt ?? '');
        return;
      }

      if (item.id === 'joingroup') {
        const username = userInfo?.username;
        let inGroup = false;

        if (JOINGROUP_KIM_GROUP_ID && username) {
          try {
            const res = await getInKimGroupApi({
              groupId: JOINGROUP_KIM_GROUP_ID,
              username,
            });
            inGroup = res.data === true;
          } catch {
            inGroup = false;
          }
        }

        if (JOINGROUP_KIM_GROUP_ID) {
          try {
            const { data: groupCount } = await getGroupClawCountApi({
              groupId: JOINGROUP_KIM_GROUP_ID,
            });
            if (typeof groupCount === 'number') {
              updateGuideState((d) => {
                d.groupBotNumber = groupCount;
              });
            }
          } catch {
            // 忽略统计接口错误，不影响弹窗展示
          }
        }

        updateGuideState((d) => {
          d.isInJoingroup = inGroup;
        });

        if (!isWbOrFl) {
          try {
            const skillStatus = await getKimoInstallCheckApi();
            updateGuideState((d) => {
              d.kimoSkillStatus = skillStatus;
            });
          } catch {
            updateGuideState((d) => {
              d.kimoSkillStatus = false;
            });
          }
          try {
            const kimoCount = await getKimoInstallCountApi();
            if (typeof kimoCount === 'number') {
              updateGuideState((d) => {
                d.kimoBotNumber = kimoCount;
              });
            }
          } catch {
            // 忽略统计接口错误，不影响弹窗展示
          }
        }

        updateGuideState((d) => {
          d.pendingJoinGroupItem = item;
          d.joinGroupKimoModalVisible = true;
        });
        return;
      }

      applyTemplate(item);
    },
    [userInfo?.username, isWbOrFl, applyTemplate, handleSubmit, updateGuideState],
  );

  const handleJoinGroupConfirm = useCallback(async () => {
    if (!guideState.pendingJoinGroupItem) return;

    if (guideState.isInJoingroup) {
      const templateText =
        guideState.pendingJoinGroupItem.template?.find((n) => n.type === 'text')
          ?.content ?? guideState.pendingJoinGroupItem.label;
      updateGuideState((d) => {
        d.joinGroupKimoModalVisible = false;
        d.pendingJoinGroupItem = null;
      });
      debouncedSubmitJoingroup(templateText);
      window.getSelection()?.removeAllRanges();
      return;
    }

    try {
      const res = await postJoinMoltBookGroupApi();
      if (res.code !== 0) {
        Toast({
          message: '尊敬的观察员，当前聊天室 🦞 名额已满，请等待下一批开放～',
          type: 'warning',
        });
        return;
      }
    } catch {
      Toast({
        message: '加入群聊失败，请稍后重试',
        type: 'error',
      });
      return;
    }

    updateGuideState((d) => {
      d.joinGroupKimoModalVisible = false;
      d.isInJoingroup = true;
      d.pendingJoinGroupItem = null;
    });
    handleSubmit('我们已经加入了 Claw 聊天室，你可以在里面做什么？');
  }, [
    guideState.pendingJoinGroupItem,
    guideState.isInJoingroup,
    debouncedSubmitJoingroup,
    handleSubmit,
    updateGuideState,
  ]);

  const handleJoinKimoConfirm = useCallback(async () => {
    if (!guideState.pendingJoinGroupItem) return;

    const kimoQuery =
      guideState.pendingJoinGroupItem.template?.[1]?.type === 'text'
        ? guideState.pendingJoinGroupItem.template[1].content
        : '帮我看看 Kimobook 社区内大家在聊什么';

    if (guideState.kimoSkillStatus) {
      updateGuideState((d) => {
        d.joinGroupKimoModalVisible = false;
        d.pendingJoinGroupItem = null;
      });
      handleSubmit(kimoQuery);
      return;
    }

    updateGuideState((d) => {
      d.kimoJoiningLoading = true;
    });
    try {
      const installResult = await postKimoInstallApi();
      if (installResult !== true) {
        throw new Error('install failed');
      }
    } catch {
      Toast({
        message: '加入 Kimobook 社区失败，请稍后重试',
        type: 'error',
      });
      updateGuideState((d) => {
        d.kimoJoiningLoading = false;
      });
      return;
    }
    updateGuideState((d) => {
      d.kimoJoiningLoading = false;
      d.kimoSkillStatus = true;
      d.joinGroupKimoModalVisible = false;
      d.pendingJoinGroupItem = null;
    });
    handleSubmit('我们已经加入了 Kimobook 社区了，你可以在里面做什么？');
  }, [
    guideState.pendingJoinGroupItem,
    guideState.kimoSkillStatus,
    handleSubmit,
    updateGuideState,
  ]);

  const handleSuggestionClick = useCallback(
    (text: string) => {
      const signature = `suggestion:${text}`;
      updateGuideState((draft) => {
        if (signature === draft.lastAppliedSignature && !draft.isEditorEmpty) return;

        draft.activeSuggestionItem = null;
        draft.activeTemplate = null;
        draft.prefilledText = {
          text,
          nonce: (draft.prefilledText?.nonce ?? 0) + 1,
        };
        draft.lastAppliedSignature = signature;
      });
    },
    [updateGuideState],
  );

  const chatBoxPlaceholderResolved = useMemo(
    () =>
      isShowPlaceholder
        ? guideState.placeholder
        : (chatBoxPlaceholder ?? GUIDE_PLACEHOLDER_DEFAULT),
    [isShowPlaceholder, guideState.placeholder, chatBoxPlaceholder],
  );

  const chatBoxTemplate = useMemo(
    () => (isShowPlaceholder ? (guideState.activeTemplate ?? undefined) : undefined),
    [isShowPlaceholder, guideState.activeTemplate],
  );

  const chatBoxPrefilledText = useMemo(
    () => (isShowPlaceholder ? (guideState.prefilledText ?? undefined) : undefined),
    [isShowPlaceholder, guideState.prefilledText],
  );

  const handleClearTemplate = useCallback(() => {
    updateGuideState((d) => {
      d.activeTemplate = null;
    });
  }, [updateGuideState]);

  const handleCloseJoinModal = useCallback(() => {
    updateGuideState((d) => {
      d.joinGroupKimoModalVisible = false;
      d.pendingJoinGroupItem = null;
    });
  }, [updateGuideState]);

  const onEditorEmptyChange = useCallback(
    (empty: boolean) => {
      updateGuideState((d) => {
        d.isEditorEmpty = empty;
      });
    },
    [updateGuideState],
  );

  const {
    activeSuggestionItem,
    clearEditorNonce,
    joinGroupKimoModalVisible,
    isInJoingroup,
    kimoSkillStatus,
    kimoJoiningLoading,
    groupBotNumber,
    kimoBotNumber,
  } = guideState;

  return {
    isWbOrFl,
    onSelectItem,
    handleSuggestionClick,
    chatBoxPlaceholderResolved,
    chatBoxTemplate,
    chatBoxPrefilledText,
    handleClearTemplate,
    handleCloseJoinModal,
    onEditorEmptyChange,
    handleJoinGroupConfirm,
    handleJoinKimoConfirm,
    activeSuggestionItem,
    clearEditorNonce,
    joinGroupKimoModalVisible,
    isInJoingroup,
    kimoSkillStatus,
    kimoJoiningLoading,
    groupBotNumber,
    kimoBotNumber,
  };
}
